/**
 * Simplified Authentication Service
 * Now uses namespaced storage to support multiple screen/masjid combinations
 * in the same browser (different tabs can have different configurations)
 */

import {
  isScreenAuthenticated,
  getCurrentScreenCode,
  getCurrentMasjidId,
  saveScreenData,
  clearScreenData,
  migrateOldStorage,
  getNamespacedValue,
  setNamespacedValue,
} from "../utils/screenStorage";

// Run migration on module load to handle existing users
migrateOldStorage();

/**
 * Check if a screen code is configured for the current tab
 */
export function isAuthenticated(): boolean {
  return isScreenAuthenticated();
}

/**
 * Get the current screen code for this tab
 */
export function getScreenCode(): string | null {
  return getCurrentScreenCode();
}

/**
 * Get the current masjid ID for this tab
 */
export function getMasjidId(): string | null {
  return getCurrentMasjidId();
}

/**
 * Store the screen code and masjid ID for this tab
 */
export function storeScreenCode(screenCode: string, masjidId?: string): void {
  if (masjidId) {
    saveScreenData({
      screenCode,
      masjidId,
      lastAuthenticated: new Date().toISOString(),
    });
  } else {
    // If no masjidId provided, try to get from current session or URL
    const params = new URLSearchParams(window.location.search);
    const urlMasjidId = params.get("masjidId") || params.get("masjid");
    if (urlMasjidId) {
      saveScreenData({
        screenCode,
        masjidId: urlMasjidId,
        lastAuthenticated: new Date().toISOString(),
      });
    }
  }
}

/**
 * Store additional data for the current screen (namespaced)
 */
export function storeScreenValue(key: string, value: string): void {
  setNamespacedValue(key, value);
}

/**
 * Get additional data for the current screen (namespaced)
 */
export function getScreenValue(key: string): string | null {
  return getNamespacedValue(key);
}

/**
 * Clear stored data and logout for the current tab
 */
export function logout(): void {
  clearScreenData();
}
